/* eslint-disable */
import { KcmTracker } from "./../trackingservice/tracking.service";
import { KcmContent, KcmNetworkOptions, KcmServiceData } from "@/types";
import { appleService } from "./apple.service";
import { facebookService } from "./facebook.service";
import { googleService } from "./google.service";
import { instagramService } from "./instagram.service";
import { linkedinService } from "./linkedin.service";
import { twitterService } from "./twitter.service";
import { pinterestService } from "./pinterest.service";
import { userService } from "@/services/internal-api/auth.service";
import store from "@/state/store";
import TrackingService, {
  KcmContentEvent,
} from "@/services/trackingservice/tracking.service";
import {
  KcmSocialPromise,
  SocialMediaServiceKey,
} from "@/types/social-media.types";

// this wrapper service can be used to perform any generic behavior before calling the social media specific service function

const services = {
  facebook: facebookService,
  instagram: instagramService,
  twitter: twitterService,
  linkedin: linkedinService,
  google: googleService,
  apple: appleService,
  pinterest: pinterestService,
};

const serviceData: { [x: string]: KcmServiceData } = {
  facebook: {
    title: "Facebook Business",
    slug: "facebook",
    icon: "facebook",
  },
  instagram: {
    title: "Instagram Business",
    slug: "instagram",
    icon: "instagram",
  },
  twitter: {
    title: "Twitter",
    icon: "x-twitter",
    slug: "twitter",
  },
  linkedin: {
    title: "LinkedIn",
    slug: "linkedin",
    icon: "linkedin",
  },
  google: {
    title: "Google",
    slug: "google",
    icon: "google",
  },
  apple: {
    title: "Apple",
    slug: "apple",
    icon: "apple",
  },
};

export const socialMediaService = {
  login,
  init,
  connect,
  disconnect,
  post,
  post_v2,
  update,
  remove,
  getPages,
  checkAccess,
  services,
  serviceData,
};

function checkAccess(
  service: (typeof services)[SocialMediaServiceKey]
): boolean {
  return service.checkAccess();
}

async function login(
  service: (typeof services)[SocialMediaServiceKey]
): Promise<KcmSocialPromise> {
  return await service.login().then((response: KcmSocialPromise) => {
    return response;
  });
}
async function init(
  service: (typeof services)[SocialMediaServiceKey]
): Promise<boolean> {
  return await service.init();
}
async function connect(
  service: (typeof services)[SocialMediaServiceKey]
): Promise<KcmSocialPromise> {
  return await service.connect().then(async (response: KcmSocialPromise) => {
    if (response) {
      if (response.success && !response.data) {
        return {
          success: true,
          data: "",
        };
      }
      if (response.success && response.data) {
        TrackingService.trackEvent("connectedSocial", { media: service.name });

        // if we're connecting to IG, save to API as FB token
        let serviceName = service.name;
        if (serviceName === "instagram") {
          serviceName = "facebook";
        }

        const apiResp = await userService.updateServiceData(
          store.getters["auth/authUserId"],
          serviceName,
          response.data
        );

        // update page names
        await getPages(service, true);

        if (apiResp) {
          return {
            success: false,
            data: apiResp,
          };
        }
        return response;
      }
    }
    return {
      success: false,
      data: "Something went wrong",
    };
  });
}
async function disconnect(
  service: (typeof services)[SocialMediaServiceKey]
): Promise<KcmSocialPromise> {
  const serviceName = service.name;
  if (serviceName === "instagram") {
    const facebookData = store.getters["auth/getSocialToken"]("facebook");
    const apiResp = await userService.updateServiceData(
      store.getters["auth/authUserId"],
      "facebook",
      {
        token: facebookData.token,
        id: facebookData.id,
        scope: facebookService.scopes,
      }
    );
    if (apiResp) {
      return {
        success: false,
        data: apiResp,
      };
    }
  } else {
    const apiResp = await userService.removeServiceData(
      store.getters["auth/authUserId"],
      serviceName
    );
    if (apiResp) {
      service.serviceData.pages = [];
      return {
        success: false,
        data: apiResp,
      };
    }
  }
  return await service.disconnect().then((response: KcmSocialPromise) => {
    service.serviceData.pages = [];
    return response;
  });
}
async function post(
  service: (typeof services)[SocialMediaServiceKey],
  content: KcmContent,
  contentType: string,
  networkOptions?: KcmNetworkOptions
): Promise<KcmSocialPromise> {
  //send this share event to our trackers
  const data: KcmContentEvent = {
    content: content,
    contentType: contentType,
    method: service.name,
  };

  let eventType = "sharedContent";
  if (networkOptions?.schedule_time) {
    eventType = "scheduledContent";
  }

  if (networkOptions?.video_length) {
    data.videoLength = networkOptions.video_length;
  }
  TrackingService.trackEvent(eventType as keyof KcmTracker, data);

  return await service.post(content, contentType, networkOptions);
}

async function post_v2(
  service: (typeof services)[SocialMediaServiceKey],
  content: KcmContent,
  networkOptions?: KcmNetworkOptions
): Promise<KcmSocialPromise> {
  return await service.post(content, content.content_type, networkOptions);
}

async function update(
  service: (typeof services)[SocialMediaServiceKey],
  networkOptions?: KcmNetworkOptions
): Promise<KcmSocialPromise> {
  return await service.update(networkOptions);
}

async function remove(
  service: (typeof services)[SocialMediaServiceKey],
  networkOptions?: KcmNetworkOptions
): Promise<KcmSocialPromise> {
  return await service.remove(networkOptions);
}

async function getPages(
  service: (typeof services)[SocialMediaServiceKey],
  breakCache?: boolean
): Promise<KcmSocialPromise> {
  if (service.serviceData.pages.length > 0 && !breakCache) {
    return { success: true, data: service.serviceData.pages };
  }
  const pages = await service.getPages();
  return pages;
}
